import {
  buildUrl,
  defaultFetchHeaders,
  fetchJson,
  getCsrfToken,
} from "../../../utils";
import { withServerTiming } from "../../perfDiagnostics";
import { liveSalesPerfEnabled } from "../../liveSalesPerf";
import {
  Machine,
  MachineCollectionSummary,
  MachineSale,
  LiveSalesCommissionStatus,
  SalesComparisonPeriod,
  PhotoAsset,
} from "../../types";
import { errorMessageFromResponse, jsonFromResponse } from "../../../apiError";
import { normalizePhotoAsset } from "../assets";
import {
  buildMachinePayload,
  normalizeMachineCollectionSummaryResponse,
  normalizeMachineResponse,
  normalizeMachineSaleResponse,
  normalizeSalesComparisonPeriods,
} from "./normalize";
import { setCsrfToken } from "../../../user/api";
import { suppressRealtimeEvent } from "../../realtime/suppressRealtime";
import { centsFromApi } from "../../currency";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const machineBase = `${backend}/machine`;
const machineSaleBase = `${machineBase}/sale`;
export type MachineSalesWindow = {
  machineId: string;
  start: string;
  end: string;
  // Pending windows keep one cache entry as their snapshot cutoff advances.
  live?: boolean;
};

export type MachineSalesTransactionPage = {
  previousPeriods?: SalesComparisonPeriod[];
  items: MachineSale[];
  nextCursor: string | null;
  hasMore: boolean;
  totals: MachineSalesTotals;
  breakdowns: MachineSalesBreakdown[];
  previousGrossCents: number | null;
};

export type MachineSalesBreakdown = MachineSalesTotals & {
  machine: string;
  machineName: string;
  location: string | null;
  locationName: string;
  startDate?: string | null;
  endDate?: string | null;
  previousGrossCents?: number | null;
  previousLocationGrossCents?: number | null;
  previousPeriods?: SalesComparisonPeriod[];
  previousLocationPeriods?: SalesComparisonPeriod[];
  locationCommissionCents?: number | null;
  locationAfterCommissionCents?: number | null;
};

export type MachineSalesTotals = {
  cashSalesCents: number;
  cardSalesCents: number;
  coinSalesCents: number;
  count: number;
  cardCount: number;
  cashCount: number;
  cardSurchargeCents?: number;
  cardFeeBaseCents?: number;
  grossSalesCents?: number;
  commissionCents?: number | null;
  afterCommissionCents?: number | null;
  commissionStatus?: LiveSalesCommissionStatus;
};

export type VoidMachineSaleResult = {
  sale: MachineSale;
  task: any;
  before: any;
  after: any;
};

const emptySalesTotals = (): MachineSalesTotals => ({
  cashSalesCents: 0,
  cardSalesCents: 0,
  coinSalesCents: 0,
  count: 0,
  cardCount: 0,
  cashCount: 0,
  cardSurchargeCents: 0,
  cardFeeBaseCents: 0,
  grossSalesCents: 0,
  commissionCents: 0,
  afterCommissionCents: 0,
  commissionStatus: "complete",
});

const optionalCents = (value: unknown): number | null =>
  value === null || value === undefined ? null : centsFromApi(value);

const commissionStatus = (value: unknown): LiveSalesCommissionStatus =>
  value === "complete" || value === "partial" || value === "not_applicable"
    ? value
    : "pending";

export type MachineSalesDailyBucket = {
  machine: string;
  location: string | null;
  date: string;
  timezone?: string;
  cashSalesCents: number;
  cardSalesCents: number;
  coinSalesCents: number;
  count: number;
  cardCount: number;
  cashCount: number;
  commissionCents?: number;
  commissionCompleteCount?: number;
  commissionNotApplicableCount?: number;
  latestSaleAt: string | null;
};

export type MachineSalesSyncResult = {
  machines: Array<{
    machine: string;
    version: number;
    resetRequired: boolean;
    sales: MachineSale[];
    summaryBuckets: MachineSalesDailyBucket[];
    collectionSummary: MachineCollectionSummary | null;
  }>;
};

export type MachineSalesSnapshotResult = {
  machines: Array<{
    machine: string;
    version: number;
    summaryBuckets: MachineSalesDailyBucket[];
  }>;
};

type ApiSalesDailyBucket = Record<string, unknown> & {
  machine?: unknown;
  location?: unknown;
  date?: unknown;
  timezone?: unknown;
  cash_sales_cents?: unknown;
  cashSalesCents?: unknown;
  card_sales_cents?: unknown;
  cardSalesCents?: unknown;
  coin_sales_cents?: unknown;
  coinSalesCents?: unknown;
  count?: unknown;
  card_count?: unknown;
  cardCount?: unknown;
  cash_count?: unknown;
  cashCount?: unknown;
  commission_cents?: unknown;
  commissionCents?: unknown;
  commission_complete_count?: unknown;
  commissionCompleteCount?: unknown;
  commission_not_applicable_count?: unknown;
  commissionNotApplicableCount?: unknown;
  card_surcharge_cents?: unknown;
  cardSurchargeCents?: unknown;
  card_fee_base_cents?: unknown;
  cardFeeBaseCents?: unknown;
  latest_sale_at?: unknown;
  latestSaleAt?: unknown;
};

type ApiSalesMachine = Record<string, unknown> & {
  machine?: unknown;
  version?: unknown;
  reset_required?: unknown;
  resetRequired?: unknown;
  sales?: unknown;
  summary_buckets?: unknown;
  summaryBuckets?: unknown;
  collection_summary?: unknown;
  collectionSummary?: unknown;
};

type ApiMachineSalesResponse = { machines?: ApiSalesMachine[] };
type ApiSalesTransactionResponse = Record<string, unknown> & {
  items?: unknown;
  breakdowns?: unknown;
  next_cursor?: unknown;
  nextCursor?: unknown;
  has_more?: unknown;
  hasMore?: unknown;
  cash_sales_cents?: unknown;
  cashSalesCents?: unknown;
  card_sales_cents?: unknown;
  cardSalesCents?: unknown;
  coin_sales_cents?: unknown;
  coinSalesCents?: unknown;
  count?: unknown;
  card_count?: unknown;
  cardCount?: unknown;
  cash_count?: unknown;
  cashCount?: unknown;
};

const normalizeSalesDailyBucket = (
  bucket: ApiSalesDailyBucket
): MachineSalesDailyBucket => ({
  machine: String(bucket.machine),
  location: bucket.location ? String(bucket.location) : null,
  date: String(bucket.date),
  timezone: String(bucket.timezone || "UTC"),
  cashSalesCents: centsFromApi(bucket.cash_sales_cents ?? bucket.cashSalesCents),
  cardSalesCents: centsFromApi(bucket.card_sales_cents ?? bucket.cardSalesCents),
  coinSalesCents: centsFromApi(bucket.coin_sales_cents ?? bucket.coinSalesCents),
  count: Number(bucket.count ?? 0) || 0,
  cardCount: Number(bucket.card_count ?? bucket.cardCount ?? 0) || 0,
  cashCount: Number(bucket.cash_count ?? bucket.cashCount ?? 0) || 0,
  commissionCents: centsFromApi(bucket.commission_cents ?? bucket.commissionCents),
  commissionCompleteCount: Number(bucket.commission_complete_count ?? bucket.commissionCompleteCount ?? 0) || 0,
  commissionNotApplicableCount: Number(bucket.commission_not_applicable_count ?? bucket.commissionNotApplicableCount ?? 0) || 0,
  latestSaleAt:
    typeof (bucket.latest_sale_at ?? bucket.latestSaleAt) === "string"
      ? String(bucket.latest_sale_at ?? bucket.latestSaleAt)
      : null,
});

const salesDailyBucketsFromApi = (value: unknown): MachineSalesDailyBucket[] =>
  Array.isArray(value)
    ? value.map((bucket) => normalizeSalesDailyBucket(bucket))
    : [];

function parseFilename(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (match && match[1]) return decodeURIComponent(match[1]);
  return fallback;
}

function buildServiceQrIdsUrl(ids: string[]) {
  const list = ids.filter(Boolean).join(",");
  return buildUrl(`${machineBase}/service-qr/bulk`, { ids: list });
}

function buildMachineIdCardsUrl(ids: string[]) {
  const list = ids.filter(Boolean).join(",");
  return buildUrl(`${machineBase}/id-card/bulk`, { ids: list });
}

const jsonHeaders = async () => ({
  ...(await defaultFetchHeaders()),
  "Content-Type": "application/json",
});

const activationChargeFailed = (payload: any): boolean => {
  const charge = payload?.activationCharge ?? payload?.activation_charge;
  const status = String(charge?.status || "").toLowerCase();
  return status.includes("failed");
};

export async function fetchMachines(): Promise<Machine[]> {
  const list = await fetchJson<Machine[]>(
    buildUrl(`${machineBase}/all`, {}),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch machines"
  );
  return list.map(normalizeMachineResponse);
}

export async function fetchMachine(id: string): Promise<Machine> {
  const machine = await fetchJson<Machine>(
    buildUrl(`${machineBase}`, { id }),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch machine"
  );
  return normalizeMachineResponse(machine);
}

export async function createMachine(payload: Machine): Promise<Machine> {
  const body = buildMachinePayload(payload);
  const created = await fetchJson<Machine>(
    `${machineBase}/create`,
    {
      method: "POST",
      credentials: "include",
      headers : await jsonHeaders(),
      body: JSON.stringify({ ...body, operationId: payload.operationId }),
    },
    "Failed to create machine"
  );
  const normalized = normalizeMachineResponse(created);
  (normalized as any).activationChargeFailed = activationChargeFailed(created);
  if (normalized.id) {
    suppressRealtimeEvent("machine.changed", normalized.id);
  }
  return normalized;
}

export async function editMachine(payload: Machine): Promise<Machine> {
  if (!payload.id) throw new Error("Machine id is required to update");
  suppressRealtimeEvent("machine.changed", payload.id);
  const body = buildMachinePayload(payload);
  const updated = await fetchJson<Machine>(
    `${machineBase}/edit?id=${payload.id}`,
    {
      method: "PUT",
      credentials: "include",
      headers : await jsonHeaders(),
      body: JSON.stringify(body),
    },
    "Failed to update machine"
  );
  return normalizeMachineResponse(updated);
}

export async function uploadMachinePhoto(
  machineId: string,
  file: File
): Promise<PhotoAsset> {
  const form = new FormData();
  form.append("file", file);
  const token = getCsrfToken() || (await setCsrfToken());
  const headers = await defaultFetchHeaders();
  delete (headers as Record<string, string>)["Content-Type"];
  const res = await fetch(
    buildUrl(`${machineBase}/asset/photo/add`, { machine_id: machineId }),
    {
      method: "POST",
      credentials: "include",
      headers: {
        ...headers,
        "X-CSRFToken": token,
      },
      body: form,
    }
  );
  if (!res.ok) {
    throw new Error(await errorMessageFromResponse(res, "Failed to upload photo"));
  }
  const asset = normalizePhotoAsset(
    await jsonFromResponse(res, "Failed to upload photo")
  );
  if (!asset) throw new Error("Failed to upload photo");
  return asset;
}

export async function copyMachinePhotoFromLocation(
  machineId: string,
  locationId: string,
  assetId: string,
): Promise<PhotoAsset> {
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(
    buildUrl(`${machineBase}/asset/photo/copy-from-location`, {
      machine_id: machineId,
      location_id: locationId,
      asset_id: assetId,
    }),
    { method: "POST", credentials: "include", headers: { ...(await defaultFetchHeaders()), "X-CSRFToken": token } },
  );
  if (!res.ok) throw new Error(await errorMessageFromResponse(res, "Failed to copy location photo"));
  const asset = normalizePhotoAsset(await jsonFromResponse(res, "Failed to copy location photo"));
  if (!asset) throw new Error("Failed to copy location photo");
  return asset;
}

export async function deleteMachineAsset(assetId: string): Promise<void> {
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(buildUrl(`${machineBase}/asset/delete`, { id: assetId }), {
    method: "DELETE",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "X-CSRFToken": token,
    },
  });
  if (!res.ok) {
    throw new Error(await errorMessageFromResponse(res, "Failed to remove photo"));
  }
}

export async function deleteMachine(id: string): Promise<{ id: string }> {
  suppressRealtimeEvent("machine.changed", id);
  return fetchJson<{ id: string }>(
    buildUrl(`${machineBase}/delete`, { id }),
    {
      method: "DELETE",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to delete machine"
  );
}

export async function activateMachine(id: string): Promise<Machine> {
  suppressRealtimeEvent("machine.changed", id);
  const result = await fetchJson<any>(
    buildUrl(`${machineBase}/activate`, { id }),
    {
      method: "PUT",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to activate machine"
  );
  const machine = normalizeMachineResponse(result);
  (machine as any).activationChargeFailed = activationChargeFailed(result);
  return machine;
}

export async function deactivateMachine(id: string): Promise<Machine> {
  suppressRealtimeEvent("machine.changed", id);
  const result = await fetchJson<Machine>(
    buildUrl(`${machineBase}/deactivate`, { id }),
    {
      method: "PUT",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to deactivate machine"
  );
  return normalizeMachineResponse(result);
}

export async function fetchMachineSalesTransactionPage({
  machineIds,
  windows,
  start,
  end,
  limit = 50,
  cursor,
  calendarRange,
  startDate,
  endDate,
  locationIds,
  includeTotals = true,
  expenseCalculationId,
  signal,
}: {
  machineIds: string[];
  windows?: MachineSalesWindow[];
  start?: string;
  end?: string;
  limit?: number;
  cursor?: string | null;
  calendarRange?: string;
  startDate?: string;
  endDate?: string;
  locationIds?: string[];
  includeTotals?: boolean;
  expenseCalculationId?: string;
  signal?: AbortSignal;
}): Promise<MachineSalesTransactionPage> {
  const uniqueIds = Array.from(new Set(machineIds.filter(Boolean))).sort();
  if (!uniqueIds.length && !expenseCalculationId) {
    return {
      items: [],
      nextCursor: null,
      hasMore: false,
      totals: emptySalesTotals(),
      breakdowns: [],
      previousGrossCents: null,
    };
  }
  const token = getCsrfToken() || (await setCsrfToken());
  const response = await fetchJson<ApiSalesTransactionResponse>(
    withServerTiming(
      `${machineSaleBase}/transactions`,
      liveSalesPerfEnabled(),
    ),
    {
      method: "POST",
      credentials: "include",
      headers: {
        ...(await jsonHeaders()),
        "X-CSRFToken": token,
      },
      body: JSON.stringify({
        machine_ids: uniqueIds,
        windows: windows?.map(({ machineId, start, end }) => ({ machine_id: machineId, start, end })),
        expense_calculation_id: expenseCalculationId || null,
        start,
        end,
        limit,
        cursor: cursor || null,
        calendar_range: calendarRange || null,
        start_date: startDate || null,
        end_date: endDate || null,
        location_ids: locationIds || [],
        include_totals: includeTotals,
      }),
      signal,
    },
    "Failed to fetch sales transactions"
  );
  return {
    items: (Array.isArray(response?.items) ? response.items : []).map(
      normalizeMachineSaleResponse
    ),
    nextCursor:
      typeof (response?.next_cursor ?? response?.nextCursor) === "string"
        ? String(response?.next_cursor ?? response?.nextCursor)
        : null,
    hasMore: Boolean(response?.has_more ?? response?.hasMore),
    totals: {
      cashSalesCents: centsFromApi(response?.cash_sales_cents ?? response?.cashSalesCents),
      cardSalesCents: centsFromApi(response?.card_sales_cents ?? response?.cardSalesCents),
      coinSalesCents: centsFromApi(response?.coin_sales_cents ?? response?.coinSalesCents),
      count: Number(response?.count ?? 0) || 0,
      cardCount: Number(response?.card_count ?? response?.cardCount ?? 0) || 0,
      cashCount: Number(response?.cash_count ?? response?.cashCount ?? 0) || 0,
      cardSurchargeCents: centsFromApi(
        response?.card_surcharge_cents ?? response?.cardSurchargeCents
      ),
      cardFeeBaseCents: centsFromApi(
        response?.card_fee_base_cents ?? response?.cardFeeBaseCents
      ),
      grossSalesCents: centsFromApi(response?.gross_sales_cents ?? response?.grossSalesCents),
      commissionCents: optionalCents(response?.commission_cents ?? response?.commissionCents),
      afterCommissionCents: optionalCents(response?.after_commission_cents ?? response?.afterCommissionCents),
      commissionStatus: commissionStatus(response?.commission_status ?? response?.commissionStatus),
    },
    breakdowns: (
      Array.isArray(response?.breakdowns) ? response.breakdowns : []
    ).map((value) => {
      const row = value as Record<string, unknown>;
      return {
        machine: String(row.machine || ""),
        machineName: String(row.machine_name ?? row.machineName ?? "Machine"),
        location: row.location ? String(row.location) : null,
        locationName: String(row.location_name ?? row.locationName ?? ""),
        cashSalesCents: centsFromApi(
          row.cash_sales_cents ?? row.cashSalesCents
        ),
        cardSalesCents: centsFromApi(
          row.card_sales_cents ?? row.cardSalesCents
        ),
        coinSalesCents: centsFromApi(
          row.coin_sales_cents ?? row.coinSalesCents
        ),
        count: Number(row.count ?? 0) || 0,
        cardCount: Number(row.card_count ?? row.cardCount ?? 0) || 0,
        cashCount: Number(row.cash_count ?? row.cashCount ?? 0) || 0,
        grossSalesCents: centsFromApi(row.gross_sales_cents ?? row.grossSalesCents),
        commissionCents: optionalCents(row.commission_cents ?? row.commissionCents),
        afterCommissionCents: optionalCents(row.after_commission_cents ?? row.afterCommissionCents),
        locationCommissionCents: optionalCents(
          row.location_commission_cents ?? row.locationCommissionCents
        ),
        locationAfterCommissionCents: optionalCents(
          row.location_after_commission_cents ?? row.locationAfterCommissionCents
        ),
        commissionStatus: commissionStatus(row.commission_status ?? row.commissionStatus),
        startDate: row.start_date ? String(row.start_date) : row.startDate ? String(row.startDate) : null,
        endDate: row.end_date ? String(row.end_date) : row.endDate ? String(row.endDate) : null,
        previousPeriods: normalizeSalesComparisonPeriods(row.previous_periods ?? row.previousPeriods),
        previousLocationPeriods: normalizeSalesComparisonPeriods(row.previous_location_periods ?? row.previousLocationPeriods),
        previousGrossCents:
          (row.previous_gross_cents ?? row.previousGrossCents) === null ||
          (row.previous_gross_cents ?? row.previousGrossCents) === undefined
            ? null
            : centsFromApi(row.previous_gross_cents ?? row.previousGrossCents),
        previousLocationGrossCents:
          (row.previous_location_gross_cents ?? row.previousLocationGrossCents) === null ||
          (row.previous_location_gross_cents ?? row.previousLocationGrossCents) === undefined
            ? null
            : centsFromApi(
                row.previous_location_gross_cents ?? row.previousLocationGrossCents
              ),
        cardSurchargeCents: centsFromApi(
          row.card_surcharge_cents ?? row.cardSurchargeCents
        ),
        cardFeeBaseCents: centsFromApi(
          row.card_fee_base_cents ?? row.cardFeeBaseCents
        ),
      };
    }),
    previousPeriods: normalizeSalesComparisonPeriods(response?.previous_periods ?? response?.previousPeriods),
    previousGrossCents:
      (response?.previous_gross_cents ?? response?.previousGrossCents) === null ||
      (response?.previous_gross_cents ?? response?.previousGrossCents) === undefined
        ? null
        : centsFromApi(response?.previous_gross_cents ?? response?.previousGrossCents),
  };
}

async function setMachineSaleVoidState({
  saleId,
  taskId,
  reason = "",
  preview = false,
  action,
}: {
  saleId: string;
  taskId: string;
  reason?: string;
  preview?: boolean;
  action: "void" | "restore";
}): Promise<VoidMachineSaleResult> {
  const token = getCsrfToken() || (await setCsrfToken());
  const response = await fetchJson<any>(
    `${machineSaleBase}/${saleId}/${action}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        ...(await jsonHeaders()),
        "X-CSRFToken": token,
      },
      body: JSON.stringify({
        task_id: taskId,
        reason,
        preview,
      }),
    },
    "Failed to void transaction"
  );
  const normalizeFinancials = (value: any) => ({
    ...value,
    grossCents: centsFromApi(value?.gross_cents ?? value?.grossCents),
    commissionCents: centsFromApi(
      value?.commission_cents ?? value?.commissionCents
    ),
    netCents: centsFromApi(value?.net_cents ?? value?.netCents),
  });
  return {
    sale: normalizeMachineSaleResponse(response.sale),
    task: response.task,
    before: normalizeFinancials(response.before),
    after: normalizeFinancials(response.after),
  };
}

export const voidMachineSale = (
  params: Omit<Parameters<typeof setMachineSaleVoidState>[0], "action">,
) => setMachineSaleVoidState({ ...params, action: "void" });

export const restoreMachineSale = (
  params: Omit<Parameters<typeof setMachineSaleVoidState>[0], "action">,
) => setMachineSaleVoidState({ ...params, action: "restore" });

export async function syncMachineSales(
  machines: Array<{ machineId: string; afterVersion: number | null }>
): Promise<MachineSalesSyncResult> {
  if (!machines.length) return { machines: [] };
  const token = getCsrfToken() || (await setCsrfToken());
  const response = await fetchJson<ApiMachineSalesResponse>(
    `${machineSaleBase}/sync`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        ...(await jsonHeaders()),
        "X-CSRFToken": token,
      },
      body: JSON.stringify({
        machines: machines.map((entry) => ({
          machine_id: entry.machineId,
          after_version: entry.afterVersion,
        })),
      }),
    },
    "Failed to synchronize live sales"
  );
  return {
    machines: (Array.isArray(response?.machines) ? response.machines : []).map(
      (entry) => ({
        machine: String(entry.machine),
        version: Number(entry.version) || 0,
        resetRequired: Boolean(entry.reset_required ?? entry.resetRequired),
        sales: (Array.isArray(entry.sales) ? entry.sales : []).map(
          normalizeMachineSaleResponse
        ),
        summaryBuckets: salesDailyBucketsFromApi(
          entry.summary_buckets ?? entry.summaryBuckets
        ),
        collectionSummary: entry.collection_summary ?? entry.collectionSummary
          ? normalizeMachineCollectionSummaryResponse(
              entry.collection_summary ?? entry.collectionSummary
            )
          : null,
      })
    ),
  };
}

export async function fetchMachineSalesSnapshot(
  machineIds: string[],
  start?: string,
  end?: string,
  calendar?: { range?: string; startDate?: string; endDate?: string }
): Promise<MachineSalesSnapshotResult> {
  const ids = Array.from(new Set(machineIds.filter(Boolean))).sort();
  if (!ids.length) return { machines: [] };
  const token = getCsrfToken() || (await setCsrfToken());
  const response = await fetchJson<ApiMachineSalesResponse>(
    withServerTiming(`${machineSaleBase}/snapshot`, liveSalesPerfEnabled()),
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await jsonHeaders()), "X-CSRFToken": token },
      body: JSON.stringify({
        machine_ids: ids,
        start,
        end,
        calendar_range: calendar?.range || null,
        start_date: calendar?.startDate || null,
        end_date: calendar?.endDate || null,
      }),
    },
    "Failed to load live sales summary"
  );
  return {
    machines: (Array.isArray(response?.machines) ? response.machines : []).map(
      (entry) => ({
        machine: String(entry.machine),
        version: Number(entry.version) || 0,
        summaryBuckets: salesDailyBucketsFromApi(
          entry.summary_buckets ?? entry.summaryBuckets
        ),
      })
    ),
  };
}

export async function fetchMachineCollectionSummaryBulk(
  ids: string[],
  signal?: AbortSignal
): Promise<Record<string, MachineCollectionSummary>> {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean))).sort();
  if (!uniqueIds.length) return {};
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetchJson<Record<string, MachineCollectionSummary>>(
    `${machineBase}/collection/summary/bulk`,
    {
      method: "POST",
      credentials: "include",
      signal,
      headers: {
        ...(await jsonHeaders()),
        "X-CSRFToken": token,
      },
      body: JSON.stringify(uniqueIds),
    },
    "Failed to fetch collection summaries"
  );
  const output = uniqueIds.reduce<Record<string, MachineCollectionSummary>>(
    (acc, id) => {
      acc[id] = normalizeMachineCollectionSummaryResponse({ machine: id });
      return acc;
    },
    {}
  );
  Object.entries(res || {}).forEach(([key, value]) => {
    output[key] = normalizeMachineCollectionSummaryResponse(value);
  });
  return output;
}

export async function downloadMachineServiceQrCodes(ids: string[]): Promise<{
  blob: Blob;
  filename: string;
}> {
  if (!ids.length) {
    throw new Error("No machines selected");
  }
  const res = await fetch(buildServiceQrIdsUrl(ids), {
    method: "GET",
    credentials: "include",
    headers : await defaultFetchHeaders(),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to download QR codes")
    );
  }
  const blob = await res.blob();
  const filename = parseFilename(
    res.headers.get("content-disposition"),
    "machine-service-qr-codes.pdf"
  );
  return { blob, filename };
}

export async function downloadMachineIdCards(ids: string[]): Promise<{
  blob: Blob;
  filename: string;
}> {
  if (!ids.length) {
    throw new Error("No machines selected");
  }
  const res = await fetch(buildMachineIdCardsUrl(ids), {
    method: "GET",
    credentials: "include",
    headers : await defaultFetchHeaders(),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to download machine IDs")
    );
  }
  const blob = await res.blob();
  const filename = parseFilename(
    res.headers.get("content-disposition"),
    "machine-ids.pdf"
  );
  return { blob, filename };
}
